import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";

/**
 * TB brand Drawer — side or bottom panel that slides in and preserves context.
 *
 * Sits at `z-250` to clear every fixed element it can be opened over: the
 * homepage header is `z-200` and its help trigger `z-100`.
 */
export const Drawer = DialogPrimitive.Root;
export const DrawerTrigger = DialogPrimitive.Trigger;
export const DrawerClose = DialogPrimitive.Close;
export const DrawerPortal = DialogPrimitive.Portal;

export const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[250] bg-[var(--tb-bg-overlay)] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
      className,
    )}
    data-drawer-overlay=""
    data-lenis-prevent=""
    {...props}
  />
));
DrawerOverlay.displayName = "DrawerOverlay";

// shadcn's default drawer animation classes reference keyframes (`tb-slide-in-right` and friends)
// that this project's CSS does not define — `animate-in`/`animate-out` from tw-animate-css instead.
const sideClasses: Record<"right" | "left" | "bottom", string> = {
  right:
    "inset-y-0 right-0 h-full w-[360px] max-w-[88%] duration-300 data-[state=open]:animate-in data-[state=open]:slide-in-from-right data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right",
  left:
    "inset-y-0 left-0 h-full w-[360px] max-w-[88%] duration-300 data-[state=open]:animate-in data-[state=open]:slide-in-from-left data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left",
  bottom:
    "inset-x-0 bottom-0 max-h-[85vh] rounded-t-[var(--tb-radius-md)] duration-300 data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom",
};

export interface DrawerContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  side?: "right" | "left" | "bottom";
  /**
   * Class for the veil. The overlay is mounted inside, so without this the only way to adjust it
   * would be a second `DrawerOverlay` alongside — which doubles the opacity, unnoticed.
   */
  overlayClassName?: string;
  /**
   * Where focus goes back to on close.
   *
   * Radix returns it to the `DrawerTrigger`, and in controlled use (no trigger) focus falls to
   * `<body>` — a silent trap, because nothing on screen shows it. Ignored when the consumer
   * brings its own `onCloseAutoFocus` (ds-variant-candidates T8).
   */
  returnFocusTo?: React.RefObject<HTMLElement | null>;
}

export const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DrawerContentProps
>(
  (
    { className, side = "right", overlayClassName, returnFocusTo, onCloseAutoFocus, children, ...props },
    ref,
  ) => (
    <DrawerPortal>
      <DrawerOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed z-[250] flex flex-col bg-tb-white shadow-[var(--tb-shadow-lg)] outline-none",
          sideClasses[side],
          className,
        )}
        data-lenis-prevent=""
        onCloseAutoFocus={
          onCloseAutoFocus ??
          (returnFocusTo
            ? (event) => {
                event.preventDefault();
                // Matches FocusScope's own unmount default (`focus(el, { select: true })`, which
                // Radix always calls with `preventScroll: true`): without it, the browser may
                // scroll to reveal the trigger, jumping the page under the lock's release.
                returnFocusTo.current?.focus({ preventScroll: true });
              }
            : undefined)
        }
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DrawerPortal>
  ),
);
DrawerContent.displayName = "DrawerContent";

export const DrawerHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex items-center justify-between border-b border-[var(--tb-hairline)] p-5", className)}
    {...props}
  />
);
DrawerHeader.displayName = "DrawerHeader";

export const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("font-display text-lg font-normal text-[var(--fg)]", className)}
    {...props}
  />
));
DrawerTitle.displayName = "DrawerTitle";

export const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-[var(--fg-muted)]", className)}
    {...props}
  />
));
DrawerDescription.displayName = "DrawerDescription";

export const DrawerBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "min-h-0 flex-1 overflow-y-auto overscroll-contain touch-pan-y p-5",
      className,
    )}
    data-lenis-prevent=""
    {...props}
  />
);
DrawerBody.displayName = "DrawerBody";

export const DrawerFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("border-t border-[var(--tb-hairline)] p-5", className)} {...props} />
);
DrawerFooter.displayName = "DrawerFooter";
